import React, { createContext, useContext, useState, useEffect } from 'react';
import { signInWithPopup, signInWithRedirect, getRedirectResult, GoogleAuthProvider, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { userBackfillEmail, userCreateOrUpdate, recordAppAccess } from '../api/userApi';
import { exchangeCalendarAuthCode as apiExchangeCalendarAuthCode, getCalendarAccessToken as apiGetCalendarAccessToken } from '../api/calendarTokenApi';
import * as readApi from '../api/readApi';
import { getProfileForGame as getProfileForGameUtil } from '../utils/gameProfile';
import { setAlgoliaUserToken } from '../services/algoliaInsightsService';
import { syncUserApprovedGroupIds } from '../services/userGroupSyncService';

const AuthContext = createContext();
const SESSION_STARTED_AT_KEY = 'gradedplay_session_started_at';
const SESSION_MAX_AGE_MS = 2 * 24 * 60 * 60 * 1000; // 2 days

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  function setSessionStartedNow() {
    try { localStorage.setItem(SESSION_STARTED_AT_KEY, String(Date.now())); } catch (_) {}
  }

  function clearSessionStarted() {
    try { localStorage.removeItem(SESSION_STARTED_AT_KEY); } catch (_) {}
  }

  function hasSessionExpired() {
    try {
      const raw = localStorage.getItem(SESSION_STARTED_AT_KEY);
      if (!raw) return false; // first run with existing firebase session: establish from now
      const startedAt = Number(raw);
      if (!Number.isFinite(startedAt) || startedAt <= 0) return false;
      return Date.now() - startedAt > SESSION_MAX_AGE_MS;
    } catch (_) {
      return false;
    }
  }

  // Note: signup and login functions removed - only Google Sign-In is used

  async function loginWithGoogle() {
    console.log('[AuthContext] loginWithGoogle start');
    const provider = new GoogleAuthProvider();
    provider.addScope('profile');
    provider.addScope('email');
    // Do not request calendar at login – consent only when user opts into calendar sync (Connect/Resync)
    provider.setCustomParameters({
      prompt: 'select_account'
    });

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      console.log('[AuthContext] loginWithGoogle success', user?.uid, user?.email);
      setSessionStartedNow();
      await userCreateOrUpdate({
        email: user.email,
        displayName: user.displayName || user.email,
        photoURL: user.photoURL || null,
        onboarded: false,
        isAdmin: false,
      });
      return result;
    } catch (error) {
      console.warn('[AuthContext] loginWithGoogle error', error?.code, error?.message);
      if (error.code === 'auth/popup-blocked') {
        // Fallback to redirect when popup is blocked (browser or strict popup blocker)
        await signInWithRedirect(auth, provider);
        return; // Page will navigate away to Google, then back with getRedirectResult
      }
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in popup was closed. Please try again.');
      }
      if (error.code === 'auth/unauthorized-domain') {
        throw new Error('This domain is not authorized. Please contact support.');
      }
      if (error.code === 'auth/user-disabled') {
        throw new Error('Logins from your user is blocked by admin.');
      }
      throw error;
    }
  }

  /** Request Google Calendar access. Uses stored OAuth (refresh token) when available so user isn't re-prompted.
   * @param {boolean} [forceConsent=false] – If true, show consent screen (first-time connect). If false, try stored token first. */
  async function requestCalendarAccess(forceConsent = false) {
    // When not forcing consent, try stored refresh token first (no popup)
    if (!forceConsent) {
      try {
        const data = await apiGetCalendarAccessToken();
        if (data?.accessToken) return data.accessToken;
      } catch (err) {
        const code = err?.code || '';
        const msg = (err?.message || '') + (err?.details || '');
        const noStoredToken = code?.includes('failed-precondition') || /NO_REFRESH_TOKEN|REFRESH_TOKEN_INVALID/.test(msg);
        if (!noStoredToken) {
          console.warn('[AuthContext] getCalendarAccessToken error', err?.message);
          throw err;
        }
        // Fall through to popup fallback
      }
    }

    // First-time connect: try GIS code flow so backend can store refresh token (no re-prompt later)
    if (forceConsent && typeof window !== 'undefined' && window.google?.accounts?.oauth2?.initCodeClient) {
      const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
      if (clientId) {
        try {
          const token = await requestCalendarAccessViaGIS(clientId);
          if (token) return token;
        } catch (err) {
          console.warn('[AuthContext] GIS calendar flow failed', err?.message);
          // Fall through to popup fallback
        }
      }
    }

    // Fallback: Firebase popup (short-lived token; no stored refresh token)
    return requestCalendarAccessViaPopup(forceConsent);
  }

  /** GIS authorization code flow: get code from Google, exchange on backend (stores refresh token). */
  function requestCalendarAccessViaGIS(clientId) {
    return new Promise((resolve, reject) => {
      const scope = 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events';
      const client = window.google.accounts.oauth2.initCodeClient({
        client_id: clientId,
        scope,
        ux_mode: 'popup',
        callback: async (response) => {
          if (response.error) {
            reject(new Error(response.error_description || response.error));
            return;
          }
          try {
            const data = await apiExchangeCalendarAuthCode({
              code: response.code,
              redirectUri: window.location.origin,
            });
            resolve(data?.accessToken ?? null);
          } catch (err) {
            reject(err);
          }
        },
        error_callback: (err) => {
          reject(new Error(err?.type === 'popup_closed' ? 'Popup closed' : err?.type || 'GIS error'));
        },
      });
      client.requestCode();
    });
  }

  /** Firebase Auth popup: returns short-lived access token (no refresh token stored). */
  async function requestCalendarAccessViaPopup(forceConsent) {
    const provider = new GoogleAuthProvider();
    provider.addScope('profile');
    provider.addScope('email');
    provider.addScope('https://www.googleapis.com/auth/calendar');
    provider.addScope('https://www.googleapis.com/auth/calendar.events');
    if (forceConsent) {
      provider.setCustomParameters({ prompt: 'consent' });
    }
    try {
      const result = await signInWithPopup(auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      return credential?.accessToken ?? null;
    } catch (err) {
      const code = err?.code || '';
      const msg = (err?.message || '').toLowerCase();
      const isPopupCancelOrBlocked =
        code === 'auth/cancelled-popup-request' ||
        code === 'auth/popup-closed-by-user' ||
        code === 'auth/popup-blocked' ||
        msg.includes('popup-blocked') ||
        msg.includes('cancelled-popup') ||
        msg.includes('popup-closed') ||
        msg.includes('internal assertion');
      if (isPopupCancelOrBlocked) {
        return null;
      }
      console.warn('[AuthContext] requestCalendarAccess popup error', err?.message);
      throw err;
    }
  }

  async function logout() {
    console.log('[AuthContext] logout');
    setAlgoliaUserToken(null);
    clearSessionStarted();
    await signOut(auth);
    setUserProfile(null);
  }

  async function checkOnboardingStatus(uid) {
    const profile = await readApi.getMyProfile();
    return profile?.onboarded ?? false;
  }

  // Handle return from redirect sign-in (used when popup was blocked)
  useEffect(() => {
    getRedirectResult(auth)
      .then(async (result) => {
        if (!result?.user) return;
        const user = result.user;
        console.log('[AuthContext] redirect sign-in success', user?.uid, user?.email);
        setSessionStartedNow();
        await userCreateOrUpdate({
          email: user.email,
          displayName: user.displayName || user.email,
          photoURL: user.photoURL || null,
          onboarded: false,
          isAdmin: false,
        });
      })
      .catch((err) => {
        if (err?.code === 'auth/user-disabled') {
          setUserProfile(null);
          try { sessionStorage.setItem('loginBlockedMessage', '1'); } catch (_) {}
        }
        console.warn('[AuthContext] getRedirectResult error', err?.code, err?.message);
      });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('[AuthContext] auth state changed', user ? { uid: user.uid, email: user.email } : 'signed out');
      if (user && hasSessionExpired()) {
        console.log('[AuthContext] session expired (>2 days), signing out');
        clearSessionStarted();
        setCurrentUser(null);
        setUserProfile(null);
        await signOut(auth);
        setLoading(false);
        return;
      }
      setCurrentUser(user);
      if (user) {
        // Existing sessions created before rollout get a fresh 2-day window.
        try {
          if (!localStorage.getItem(SESSION_STARTED_AT_KEY)) setSessionStartedNow();
        } catch (_) {}
        setAlgoliaUserToken(user.uid);
        try {
          let profile = await readApi.getMyProfile();
          if (!profile) {
            await userCreateOrUpdate({
              email: user.email,
              displayName: user.displayName || user.email,
              photoURL: user.photoURL || null,
              onboarded: false,
              isAdmin: false,
            });
            profile = await readApi.getMyProfile();
          }
          if (profile?.loginBlocked) {
            try { sessionStorage.setItem('loginBlockedMessage', '1'); } catch (_) {}
            setUserProfile(null);
            await signOut(auth);
            setCurrentUser(null);
            return;
          }
          if (profile) {
            if (!profile.email && user.email) {
              try {
                await userBackfillEmail(user.email);
              } catch (e) {
                console.warn('Could not backfill email:', e?.message);
              }
            }
            syncUserApprovedGroupIds(user.uid).catch(() => {});
            setUserProfile(profile);
            recordAppAccess(profile).catch((e) => console.warn('[AuthContext] recordAppAccess failed:', e?.message));
            console.log('✅ User profile loaded (via cloud function)');
          } else {
            setUserProfile(null);
          }
        } catch (error) {
          console.error('❌ Error loading user profile:', error);
          setUserProfile(null);
        }
      } else {
        clearSessionStarted();
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  async function refreshUserProfile() {
    if (!currentUser) return;
    try {
      const profile = await readApi.getMyProfile();
      if (profile) setUserProfile(profile);
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }
  }

  /** Get merged profile for a game (skills and game-specific properties). */
  function getProfileForGame(profile, gameId) {
    return getProfileForGameUtil(profile || userProfile, gameId);
  }

  const value = {
    currentUser,
    userProfile,
    getProfileForGame,
    loginWithGoogle,
    requestCalendarAccess,
    logout,
    checkOnboardingStatus,
    refreshUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

